import File from '@/components/DesignLibrary/File';
import CustomSelect from '@/components/FormFields/CustomSelect';
import { useState } from 'react';
import CaretDown from '@/components/Icons/CaretDown';
import ArrowUp from '@/components/Icons/ArrowUp';
import ArrowDown from '@/components/Icons/ArrowDown';
import Buttons from '@/components/Buttons';
import classNames from 'classnames';

import s from './DocumentSort.module.scss';

type BaseDocument = Parameters<typeof File>[0]['document'];

type Props<D> = {
  onChange: (files: Array<D>) => void;
  files: D[];
  className?: string;
};

const DocumentSort = <D extends BaseDocument>({ onChange, files, className }: Props<D>): React.ReactElement => {
  const [isDescending, setIsDescending] = useState(true);
  const [sortField, setSortField] = useState<'date' | 'name'>('date');

  const sortFiles = (field: 'date' | 'name', order: boolean): void => {
    let sorted;
    if (field === 'date') {
      sorted = [...files].sort(
        (a, b) =>
          order
            ? new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime()
            : new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
      );
    } else {
      sorted = [...files].sort(
        (a, b) =>
          order
            ? a.fileName.localeCompare(b.fileName)
            : b.fileName.localeCompare(a.fileName),
      );
    }
    onChange(sorted);
  };

  const handleSortChange = (field: 'date' | 'name'): void => {
    setSortField(field);
    sortFiles(field, isDescending);
  };

  const handleOrderToggle = (): void => {
    const newOrder = !isDescending;
    setIsDescending(newOrder);
    sortFiles(sortField, newOrder);
  };

  return (
    <div className={classNames(s.wrapper, className)}>
      <CustomSelect
        id="sort-by"
        value=""
        trigger={
          <p className="caption">
            Sort by <CaretDown size={10} />
          </p>
        }
        options={[
          {
            title: 'Upload date',
            value: 'date',
          },
          {
            title: 'File name',
            value: 'name',
          },
        ]}
        onChange={(value: string) => {
          handleSortChange(value as 'date' | 'name');
        }}
      />
      <Buttons isWrapper onClick={handleOrderToggle} className='caption'>
        {isDescending ? <><ArrowDown />Desc</> : <><ArrowUp />Asc</>}
      </Buttons>
    </div>
  );
};

export default DocumentSort;
